import { yClamp } from '../../y/math';
import { onAttach, onDetach } from '../attach';
import { uiPlacehold } from '../functions';
import { UIOrderDelegate } from '../order';
import { UIPoint } from '../types';
import { UIViewSpec } from '../view';
import { UIAdapter, UIAdapterObserver } from './adapter';
import { UIList } from './list';

export interface UIListLayout<T extends UIViewSpec = UIViewSpec> {
    attach(list: UIList<T>): void;
    init(sections: UIAdapter<T>[]): void;
    onOrder(view: UIViewSpec): UIOrderDelegate | undefined;
}

export interface UIListSection<T extends UIViewSpec = UIViewSpec> {
    adapter: UIAdapter<T>;
    index: number;
    header?: UIViewSpec;
    views: T[];
}

export class UIBasicLayout implements UIAdapterObserver, UIListLayout {
    protected _list!: UIList;
    protected _sections: UIListSection[];

    constructor() {
        this._sections = [];
    }

    attach(list: UIList) {
        this._list = list;
    }

    protected _clearSection(s: UIListSection) {
        if(s.header) {
            // $log('app', 'clear header', s.index);
            s.header.remove();
            s.header = undefined;
        }

        for(const v of s.views) {
            // $log('app', 'clear section', s.index, v);
            v.remove();
        }
        s.views = [];
    }

    protected _getEnd(index: number) {
        const sections = this._sections;
        for(let i = index + 1; i < sections.length; i++) {
            const s = sections[i];
            if(s.header) return s.header;
            if(s.views.length) return s.views[0];
        }

        return;
    }

    protected _init(adapters: UIAdapter[]) {
        for(const s of this._sections) {
            this._clearSection(s);
            s.adapter.detach(this);
        }

        this._sections = adapters.map((a, i) => {
            a.attach(this, i);
            return { index: i, adapter: a, views: [] }
        });
    }

    protected _fillSection(s: UIListSection) {
        // $log('app', 'fill section', s)
        const list = this._list;
        const end = this._getEnd(s.index);

        const a = s.adapter;
        const header = a.getHeader();
        if(header) {
            // $log('app', 'add header', s.index, header);
            s.header = header;
            list.ui_insert(s.header, end);
        }
        const count = a.count;
        for(let i = 0; i < count; i++) {
            // $log('app', 'add section', s.index, i);
            const v = a.getView(i);
            list.ui_insert(v, end);
            s.views.push(v);
        }
    }

    protected _findView(view: UIViewSpec): [UIListSection, number] | undefined {
        for(const section of this._sections) {
            const index = section.views.indexOf(view);
            if(index >= 0)
                return [section, index];
        }

        return;
    }

    init(adapters: UIAdapter[]) {
        // $log('app', 'basic init', adapters);
        this._init(adapters);

        for(const s of this._sections) {
            // $log('app', 'gonna fill section', s);
            this._fillSection(s);
        }
    }

    onItemAdded(i: number, a: UIAdapter) {
        const section = this._sections[a.section];

        const v = section.adapter.getView(i);
        this._list.ui_insert(v, section.views[i] || this._getEnd(section.index));
        section.views.splice(i, 0, v);
    }

    onItemRemoved(i: number, a: UIAdapter) {
        // $log('layout', 'removed', a.section, i);
        const section = this._sections[a.section];

        section.views[i].remove();
        section.views.splice(i, 1);
    }

    onItemUpdated(i: number, a: UIAdapter) {
        // $log('layout', 'updated', a.section, i);
        const section = this._sections[a.section];

        const v = section.adapter.getView(i);

        if(v != section.views[i]) {
            section.views[i].ui_replace(v);
            section.views[i] = v;
        }
    }

    onItemMoved(from: number, to: number, a: UIAdapter) {
        // $log('layout', 'moved', from, to, a.section);
        const section = this._sections[a.section];

        const views = section.views;
        this._list.ui_insert(views[from], views[to < from ? to : to + 1]);
        views.splice(to, 0, views.splice(from, 1)[0]);
    }

    onItemsChanged(a: UIAdapter) {
        // $log('app', 'basic layout changed', a.section);
        const section = this._sections[a.section];

        this._clearSection(section);
        this._fillSection(section);
    }

    onOrder(view: UIViewSpec) {
        const found = this._findView(view);
        if(!found) return;

        const section = found[0];
        const views = section.views;
        if(views.length <= 1) return;
        const initial_index = found[1];
        let last_index = initial_index;
        const list = this._list;

        const centers: UIPoint[] = views.map(v => {
            const block = v.ui_elem;
            return [block.offsetLeft + block.offsetWidth / 2, block.offsetTop + block.offsetHeight / 2];
        });

        const parent_elem = list.ui_elem;
        const view_elem = view.ui_elem;

        const parent_rect = parent_elem.getBoundingClientRect();
        const view_rect = view_elem.getBoundingClientRect();
        const parent_size = [parent_elem.offsetWidth, parent_elem.offsetHeight];
        const view_size = [view_elem.offsetWidth, view_elem.offsetHeight];
        const scale = [parent_size[0] / parent_rect.width, parent_size[1] / parent_rect.height];
        const parent_origin = [view_elem.offsetLeft - (view_rect.left - parent_rect.left) * scale[0], view_elem.offsetTop - (view_rect.top - parent_rect.top) * scale[1]];
        const initial_pos = [view_elem.offsetLeft, view_elem.offsetTop];

        let placeholder: HTMLDivElement;

        function calcIndex(left: number, top: number) {
            if(top < initial_pos[1]) {
                for(let i = 0; i < centers.length; i++) {
                    const y = centers[i][1];
                    if(top < y) {
                        for(; i < centers.length; i++) {
                            if(left < centers[i][0])
                                return i;
                            if(centers[i][1] > y + view_size[1] / 2)
                                return i - 1;
                        }
                    }
                }

                return centers.length - 1;
            } else {
                const bottom = top + view_size[1];
                const right = left + view_size[0];
                for(let i = centers.length - 1; i >= 0; i--) {
                    const y = centers[i][1];
                    if(bottom > y) {
                        for(; i >= 0; i--) {
                            if(right > centers[i][0])
                                return i;
                            if(centers[i][1] < y - view_size[1] / 2)
                                return i + 1;
                        }
                    }
                }

                return 0;
            }
        }

        const saved_width = view_elem.style.width;
        const saved_height = view_elem.style.height;
        const saved_left = view_elem.style.left;
        const saved_top = view_elem.style.top;

        return {
            orderStart: () => {
                placeholder = uiPlacehold(view_elem);
                view.classList.add('x-dragging');
                list.ui_insert(placeholder, view);
            },
            orderMove: (offset: UIPoint) => {
                offset = [offset[0] * scale[0], offset[1] * scale[1]];
                const pos: UIPoint = [
                    yClamp(offset[0] + initial_pos[0], parent_origin[0], parent_origin[0] + parent_size[0] - view_size[0]),
                    yClamp(offset[1] + initial_pos[1], parent_origin[1], parent_origin[1] + parent_size[1] - view_size[1])
                ];
                view_elem.style.left = pos[0] + 'px';
                view_elem.style.top = pos[1] + 'px';

                const new_index = calcIndex(pos[0], pos[1]);

                if(new_index !== last_index) {
                    last_index = new_index;
                    list.ui_insert(placeholder, views[new_index < initial_index ? new_index : new_index + 1]);
                }

                return view_elem.getBoundingClientRect();
            },
            orderEnd: () => {
                view.classList.remove('x-dragging');
                view_elem.style.width = saved_width;
                view_elem.style.height = saved_height;
                view_elem.style.left = saved_left;
                view_elem.style.top = saved_top;
                placeholder.remove();

                if(last_index != initial_index) {
                    section.adapter.onViewMoved(initial_index, last_index);
                }
            }
        }
    }
}

export class UIScrollLayout extends UIBasicLayout {
    private _more?: boolean;
    private _next_s: number;
    private _next_i: number;
    private _to_add_pending: number;

    constructor() {
        super()

        this._next_s = 0;
        this._next_i = -1;
        this._to_add_pending = 0;
    }

    override attach(list: UIList) {
        super.attach(list);

        const on_scroll = () => {
            if(!this._more) return;

            const rect = list.ui_elem.getBoundingClientRect();
            if(rect.bottom < 3 * window.innerHeight) {
                this._loadViews(12);
            }
        }

        onAttach(list, () => {
            window.addEventListener('scroll', on_scroll);
        });

        onDetach(list, () => {
            window.removeEventListener('scroll', on_scroll);
        });
    }

    override init(sections: UIAdapter[]) {
        super._init(sections);

        this._next_s = 0;
        this._next_i = -1;
        this._to_add_pending = 0;
        // $log('app', 'adding progressive', sections);
        this._loadViews(48);
    }

    private _loadViews(to_add: number) {
        // $log('app', 'load views to', to_add);
        if(!to_add) return;

        let next_i = this._next_i

        const sections = this._sections;
        const list = this._list;

        for(let s = this._next_s; s < sections.length; s++) {
            const section = sections[s];
            const adapter = section.adapter;
            const end = this._getEnd(s);

            if(next_i < 0) {
                const header = adapter.getHeader();
                if(header) {
                    section.header = header;
                    list.ui_add(header);
                }
                next_i = 0;
            }

            const count = adapter.count

            for(let i = next_i; i < count; i++) {
                const v = adapter.getView(i);
                list.ui_insert(v, end);
                section.views.push(v);

                if(--to_add <= 0) {
                    this._more = true;
                    this._next_s = s;
                    this._next_i = i + 1;
                    return;
                }
            }

            next_i = -1;
        }

        this._more = false;
        if(sections.length) {
            this._next_s = sections.length - 1;
            this._next_i = sections[this._next_s].adapter.count - 1;
            this._to_add_pending = to_add;
        } else {
            this._next_s = sections.length;
            this._next_i = -1;
            this._to_add_pending = 0;
        }

    }

    private _isVisible(s: number, i: number) {
        return s < this._next_s || (s == this._next_s && i < this._next_i);
    }

    override onItemAdded(i: number, a: UIAdapter) {
        if(this._isVisible(a.section, i))
            super.onItemAdded(i, a);
    }

    override onItemRemoved(i: number, a: UIAdapter) {
        // $log('layout', 'removed', a.section, i);
        const s = a.section;
        if(this._isVisible(s, i)) {
            super.onItemRemoved(i, a);
            if(s == this._next_s)
                this._next_i--;
        }
    }

    override onItemUpdated(i: number, a: UIAdapter) {
        // $log('layout', 'updated', a.section, i);
        if(this._isVisible(a.section, i))
            super.onItemUpdated(i, a);
    }

    override onItemMoved(from: number, to: number, a: UIAdapter) {
        // $log('layout', 'moved', from, to, a.section);
        super.onItemMoved(from, to, a);
        // TODO
    }

    override onItemsChanged(a: UIAdapter) {
        // $log('app', 'layout changed', a.section, this._next_s, a.section, this._next_i);
        const s = a.section;
        const section = this._sections[s];
        if(s > this._next_s) return;

        if(s == this._next_s) {
            const count = section.views.length + this._to_add_pending;
            this._clearSection(section);
            this._next_i = -1;
            this._loadViews(count);
        } else {
            this._clearSection(section);
            this._fillSection(section);
        }
    }
}

export interface UIGridPositionable {
    getGridPos(): UIPoint;
    setGridPos(p: UIPoint): void;
}

export class UIAnchorGridLayout implements UIListLayout<UIViewSpec & UIGridPositionable> {
    protected _list!: UIList<UIViewSpec & UIGridPositionable>;
    protected _sections: UIListSection<UIViewSpec & UIGridPositionable>[];

    private _rows: number[];
    private _cols: number[];

    constructor(rows: number[], cols: number[]) {
        this._sections = [];
        this._rows = rows;
        this._cols = cols;
    }

    attach(list: UIList<UIViewSpec & UIGridPositionable>) {
        this._list = list;
    }

    protected _clearSection(s: UIListSection) {
        if(s.header) {
            s.header.remove();
            s.header = undefined;
        }

        for(const v of s.views) {
            v.remove();
        }
        s.views = [];
    }

    protected _getEnd(index: number) {
        const sections = this._sections;
        for(let i = index + 1; i < sections.length; i++) {
            const s = sections[i];
            if(s.header) return s.header;
            if(s.views.length) return s.views[0];
        }

        return;
    }

    protected _init(adapters: UIAdapter<UIViewSpec & UIGridPositionable>[]) {
        for(const s of this._sections) {
            this._clearSection(s);
            s.adapter.detach(this);
        }

        this._sections = adapters.map((a, i) => {
            a.attach(this, i);
            return { index: i, adapter: a, views: [] }
        });
    }

    private _position(view: UIViewSpec & UIGridPositionable) {
        const elem = view.ui_elem;
        const pos = view.getGridPos();

        elem.style.top = Math.round(this._rows[pos[0]]) + 'px';
        elem.style.left = Math.round(this._cols[pos[1]]) + 'px';
    }

    protected _fillSection(s: UIListSection<UIViewSpec & UIGridPositionable>) {
        const list = this._list;
        const end = this._getEnd(s.index);

        const a = s.adapter;
        const header = a.getHeader();
        if(header) {
            list.ui_insert(header, end);
        }
        const count = a.count;
        for(let i = 0; i < count; i++) {
            const v = a.getView(i);
            v.classList.add('x-absolute');
            list.ui_insert(v, end);
            s.views.push(v);
            this._position(v);
        }
    }

    protected _findView(view: UIViewSpec & UIGridPositionable): [UIListSection, number] | undefined {
        for(const section of this._sections) {
            const index = section.views.indexOf(view);
            if(index >= 0)
                return [section, index];
        }

        return;
    }

    init(adapters: UIAdapter<UIViewSpec & UIGridPositionable>[]) {
        this._init(adapters);

        for(const s of this._sections) {
            this._fillSection(s);
        }
    }

    onItemAdded(i: number, a: UIAdapter) {
        const section = this._sections[a.section];

        const v = section.adapter.getView(i);
        v.classList.add('x-absolute');
        this._list.ui_insert(v, section.views[i] || this._getEnd(section.index));
        section.views.splice(i, 0, v);
    }

    onItemRemoved(i: number, a: UIAdapter) {
        // $log('layout', 'removed', a.section, i);
        const section = this._sections[a.section];

        section.views[i].remove();
        section.views.splice(i, 1);
    }

    onItemUpdated(i: number, a: UIAdapter) {
        // $log('layout', 'updated', a.section, i);
        const section = this._sections[a.section];

        const v = section.adapter.getView(i);

        if(v != section.views[i]) {
            section.views[i].ui_replace(v);
            section.views[i] = v;
        }
    }

    onItemMoved(from: number, to: number, a: UIAdapter) {
        // $log('layout', 'moved', from, to, a.section);
        const section = this._sections[a.section];

        const views = section.views;
        this._list.ui_insert(views[from], views[to < from ? to : to + 1]);
        views.splice(to, 0, views.splice(from, 1)[0]);
    }

    onItemsChanged(a: UIAdapter) {
        // $log('app', 'changed', a.section);
        const section = this._sections[a.section];

        this._clearSection(section);
        this._fillSection(section);
    }

    onOrder(view: UIViewSpec & UIGridPositionable) {
        const found = this._findView(view);
        if(!found) return;

        const section = found[0];
        const views = section.views;
        if(views.length <= 1) return;
        const initial_index = found[1];
        let last_index = initial_index;
        const list = this._list;

        const centers: UIPoint[] = views.map(v => {
            const block = v.ui_elem;
            return [block.offsetLeft + block.offsetWidth / 2, block.offsetTop + block.offsetHeight / 2] as UIPoint;
        });

        const parent_elem = list.ui_elem;
        const view_elem = view.ui_elem;
        const view_rect = view_elem.getBoundingClientRect();
        const offset_origin = [view_rect.left - view_elem.offsetLeft, view_rect.top - view_elem.offsetTop];
        const parent_rect = parent_elem.getBoundingClientRect();
        const parent_origin = [parent_rect.left - offset_origin[0], parent_rect.top - offset_origin[1]];
        const parent_size = [parent_rect.width, parent_rect.height];
        const view_size = [view_rect.width, view_rect.height];
        const initial_pos = [view_elem.offsetLeft, view_elem.offsetTop];
        let placeholder: HTMLDivElement;

        function calcIndex(left: number, top: number) {
            let i;
            const height = view_size[1] / 2;
            for(i = 0; i < centers.length; i++) {
                const y = centers[i][1];
                if(top < y) {
                    for(; i < centers.length; i++) {
                        if(left < centers[i][0])
                            return i;
                        if(centers[i][1] > y + height)
                            return i - 1;
                    }
                }
            }

            return i - 1;
        }

        return {
            orderStart: () => {
                placeholder = uiPlacehold(view_elem);
                view.classList.add('x-dragging');
                list.ui_insert(placeholder, view);
            },
            orderMove: (offset: UIPoint) => {
                // $log('layout', '%O', offset);
                const pos: UIPoint = [
                    yClamp(offset[0] + initial_pos[0], parent_origin[0], parent_origin[0] + parent_size[0] - view_size[0]),
                    yClamp(offset[1] + initial_pos[1], parent_origin[1], parent_origin[1] + parent_size[1] - view_size[1])
                ];
                view_elem.style.left = pos[0] + 'px';
                view_elem.style.top = pos[1] + 'px';

                const new_index = calcIndex(pos[0], pos[1]);

                if(new_index !== last_index) {
                    last_index = new_index;
                    list.ui_insert(placeholder, views[new_index < initial_index ? new_index : new_index + 1]);
                }

                return view_elem.getBoundingClientRect();
            },
            orderEnd: () => {
                view.classList.remove('x-dragging');
                view_elem.style.width = '';
                view_elem.style.height = '';
                view_elem.style.left = '';
                view_elem.style.top = '';
                placeholder.remove();

                if(last_index != initial_index) {
                    section.adapter.onViewMoved(initial_index, last_index);
                }
            }
        }
    }
}
